// ui/components/DataToolbar.tsx
import React from "react";

export type DataToolbarProps = React.PropsWithChildren<{
  title?: React.ReactNode;
  role?: string; // compatibility (some pages pass role but DataToolbar doesn't need it)

  canEdit?: boolean;

  // Create (backward compatible aliases)
  onCreate?: () => void;
  onAdd?: () => void;
  onNew?: () => void;
  createLabel?: string;
  addLabel?: string;
  newLabel?: string;

  // Export
  onExport?: () => void;
  exportLabel?: string;

  // Optional search (不影響既有頁面；有傳才顯示)
  onSearch?: (q: string) => void;
  searchValue?: string;
  placeholder?: string;

  // Extra buttons (e.g. super/admin actions)
  extraButtons?: React.ReactNode;
}>;

export function DataToolbar(props: DataToolbarProps) {
  const {
    title,
    canEdit = false,

    onCreate,
    onAdd,
    onNew,
    createLabel,
    addLabel,
    newLabel,

    onExport,
    exportLabel = "匯出",

    onSearch,
    searchValue = "",
    placeholder = "搜尋…",

    extraButtons,
    children,
  } = props;

  const handleCreate = onCreate ?? onAdd ?? onNew;
  const finalCreateLabel = createLabel ?? addLabel ?? newLabel ?? "新增資料";

  const btnStyle: React.CSSProperties = { width: "auto", marginTop: 0 };

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 12,
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center", flex: 1, minWidth: 0 }}>
        {title ? (
          <div style={{ fontSize: 18, fontWeight: 700, whiteSpace: "nowrap" }}>{title}</div>
        ) : null}

        {onSearch ? (
          <input
            className="input"
            style={{ maxWidth: 420 }}
            value={searchValue}
            placeholder={placeholder}
            onChange={(e) => onSearch(e.target.value)}
          />
        ) : null}

        {children}
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        {extraButtons}

        {canEdit && handleCreate ? (
          <button className="btn" onClick={handleCreate} style={btnStyle}>
            + {finalCreateLabel}
          </button>
        ) : null}

        {onExport ? (
          <button className="btn" onClick={onExport} style={btnStyle}>
            {exportLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export type ActionButtonsProps = {
  role?: string; // compatibility
  canEdit?: boolean;

  onPreview?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;

  previewLabel?: string;
  editLabel?: string;
  deleteLabel?: string;

  // Optional override for cases where delete is allowed even if canEdit=false
  canDelete?: boolean;
};

export function ActionButtons({
  canEdit = false,
  canDelete,
  onPreview,
  onEdit,
  onDelete,
  previewLabel = "預覽",
  editLabel = "編輯",
  deleteLabel = "刪除",
}: ActionButtonsProps) {
  const allowDelete = canDelete ?? canEdit;

  const btnStyle: React.CSSProperties = { width: "auto", marginTop: 0, padding: "8px 10px" };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
      {onPreview ? (
        <button className="btn" style={btnStyle} onClick={onPreview}>
          {previewLabel}
        </button>
      ) : null}

      {canEdit && onEdit ? (
        <button className="btn" style={btnStyle} onClick={onEdit}>
          {editLabel}
        </button>
      ) : null}

      {allowDelete && onDelete ? (
        <button className="btn" style={btnStyle} onClick={onDelete}>
          {deleteLabel}
        </button>
      ) : null}
    </div>
  );
}
